import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import NodeModal from './NodeModal';
import CustomNode from './CustomNode';
import CustomEdge from './CustomEdge';
import GlobalPromptModal from './GlobalPromptModal';
import AddNodePanel from './AddNodePanel';
import ResponsiveMiniMap from './ResponsiveMiniMap';
import { DialogFlowPayloadManager } from '../utils/dialogFlowPayloadManager';
import { getLayoutedElements, NODE_DIMENSIONS } from '../utils/layoutManager';
import { Button } from './common/ui';

// Define node types
const nodeTypes = {
  'global': CustomNode,
  'Default': CustomNode,
  'End Call': CustomNode,
  'Transfer Call': CustomNode,
  'Vector DB Knowledge Base': CustomNode,
  'Webhook': CustomNode,
  'Transfer Dialog Flow': CustomNode,
  'Custom Tool': CustomNode,
  'Press Button': CustomNode,
  'Wait for Response': CustomNode,
};

// Define edge types
const edgeTypes = {
  custom: CustomEdge,
};

const FlowEditor = ({ 
  initialNodes, 
  initialEdges, 
  initialGlobalPrompt = '', 
  onSave,
  dialogFlowName = 'Unnamed dialogFlow',
  dialogFlowId = '',
  onBack,
  isSaving = false
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeMenuPosition, setNodeMenuPosition] = useState(null);
  const [isGlobalPromptOpen, setIsGlobalPromptOpen] = useState(false);
  const [globalPrompt, setGlobalPrompt] = useState(initialGlobalPrompt);
  const [isAddNodeOpen, setIsAddNodeOpen] = useState(false);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [jsonContent, setJsonContent] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [jsonEditorWidth, setJsonEditorWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);
  const reactFlowInstance = useRef(null);
  const isConnecting = useRef(false);

  // Move handleEdgeLabelChange to the top, before it's used
  const handleEdgeLabelChange = useCallback((edgeId, newLabel) => {
    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.id === edgeId) {
          return {
            ...edge,
            data: {
              ...edge.data,
              label: newLabel,
            },
          };
        }
        return edge;
      })
    );
  }, [setEdges]);

  // Initialize nodes and edges with formatted and layouted data
  useEffect(() => {
    if (initialNodes && initialEdges) {
      try {
        const nodesArray = Array.isArray(initialNodes) ? initialNodes : [];
        const edgesArray = Array.isArray(initialEdges) ? initialEdges : [];
        
        // Preserve all node data when formatting
        const formattedNodes = nodesArray.map(node => {
          const baseNode = DialogFlowPayloadManager.formatNodes([node])[0];
          return {
            ...baseNode,
            data: {
              ...baseNode.data,
              ...node.data, // Preserve all original data
              type: node.type || node.data.type,
              label: node.data.name,
              // Explicitly preserve loop condition fields
              condition: node.data.condition,
              conditionExamples: node.data.conditionExamples,
              loopConditionEnabled: node.data.loopConditionEnabled,
              // Preserve other important fields
              isGlobal: !!node.data.isGlobal,
              globalPrompt: node.data.globalPrompt,
              modelOptions: node.data.modelOptions,
              isStart: node.data.isStart
            }
          };
        });

        const formattedEdges = DialogFlowPayloadManager.formatEdges(edgesArray).map(edge => ({
          ...edge,
          data: {
            ...edge.data,
            onLabelChange: handleEdgeLabelChange
          }
        }));
        
        if (formattedNodes.length > 0) {
          const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
            formattedNodes,
            formattedEdges
          );
          setNodes(layoutedNodes);
          setEdges(layoutedEdges);
        } else {
          setNodes([]);
          setEdges([]);
        }
      } catch (error) {
        console.error('Error formatting nodes/edges:', error);
        setNodes([]);
        setEdges([]);
      }
    }
  }, [initialNodes, initialEdges, setEdges, setNodes, handleEdgeLabelChange]);

  useEffect(() => {
    setGlobalPrompt(initialGlobalPrompt);
  }, [initialGlobalPrompt]);

  const handleSaveGlobalPrompt = (newPrompt) => {
    setGlobalPrompt(newPrompt);
  };

  // Add JSON editor update effect
  useEffect(() => {
    const flowData = {
      nodes: nodes,
      edges: edges,
      globalPrompt: globalPrompt
    };
    setJsonContent(JSON.stringify(flowData, null, 2));
  }, [nodes, edges, globalPrompt]);

  // Add JSON editor change handler
  const handleJsonChange = (event) => {
    const newJsonContent = event.target.value;
    setJsonContent(newJsonContent);
    try {
      const parsedData = JSON.parse(newJsonContent);
      setJsonError(null);
      
      if (parsedData.nodes) setNodes(parsedData.nodes);
      if (parsedData.edges) setEdges(parsedData.edges);
      if (parsedData.globalPrompt) setGlobalPrompt(parsedData.globalPrompt);
    } catch (error) {
      setJsonError('Invalid JSON format');
    }
  };

  // Add toggle JSON editor handler
  const toggleJsonEditor = () => {
    setShowJsonEditor(!showJsonEditor);
  };

  // Add edge deletion handler
  const onEdgesDelete = useCallback(
    (edgesToDelete) => {
      setEdges((eds) => eds.filter((edge) => !edgesToDelete.find((e) => e.id === edge.id)));
    },
    [setEdges]
  );

  // Update handleConnect function to handle self-connections properly
  const handleConnect = useCallback(
    (params) => {
      if (!params.source || !params.target) return;

      const edgeId = `edge-${Date.now()}`;
      const newEdge = {
        id: edgeId,
        source: params.source,
        target: params.target,
        type: 'custom',
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        style: params.source === params.target ? {
          strokeWidth: 2,
          stroke: '#555',
        } : undefined,
        data: { 
          label: 'User Responded',
          onLabelChange: handleEdgeLabelChange,
          isSelfConnecting: params.source === params.target
        }
      };

      setEdges((eds) => [...eds, newEdge]);
    },
    [handleEdgeLabelChange, setEdges]
  );

  // Update the handleSave function
  const handleSave = useCallback(() => {
    // Format nodes for API
    const formattedNodes = nodes.map(node => {
      const formattedNode = DialogFlowPayloadManager.formatNodeForApi(node);
      return {
        ...formattedNode,
        data: {
          ...formattedNode.data,
          ...node.data // Preserve all node data
        }
      };
    });

    // Format edges using the edge formatter
    const formattedEdges = edges.map(edge => DialogFlowPayloadManager.formatEdgeForApi(edge));

    // Call the onSave callback with formatted data
    onSave({
      name: dialogFlowName,
      nodes: formattedNodes,
      edges: formattedEdges,
      globalPrompt: globalPrompt || ''
    });
  }, [nodes, edges, globalPrompt, dialogFlowName, onSave]);

  // Add layout direction buttons
  const onLayout = (direction) => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes,
      edges,
      direction
    );
    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
  };

  // Add connection start handler
  const onConnectStart = useCallback(() => {
    isConnecting.current = true;
  }, []);

  // Add connection end handler
  const onConnectEnd = useCallback(() => {
    setTimeout(() => {
      isConnecting.current = false;
    }, 100);
  }, []);

  // Update onNodeClick handler
  const onNodeClick = useCallback(
    (event, clickedNode) => {
      // Don't show menu if we're making a connection
      if (isConnecting.current) {
        return;
      }

      // Get the node's DOM element
      const reactFlowNode = document.querySelector(`[data-id="${clickedNode.id}"]`);
      if (reactFlowNode) {
        const rect = reactFlowNode.getBoundingClientRect();
        setSelectedNode(clickedNode);
        setNodeMenuPosition({
          x: rect.right + 10,
          y: rect.top,
        });
      }
    },
    []
  );

  // Add a new handler for node selection
  const onSelectionChange = useCallback(
    ({ nodes: selectedNodes }) => {
      if (!selectedNodes?.length) {
        setNodeMenuPosition(null);
        setSelectedNode(null);
      }
    },
    []
  );

  const handleCloseNodeMenu = () => {
    setNodeMenuPosition(null);
  };

  const handleDeleteNode = useCallback(
    (nodeId) => {
      setNodes((nds) => nds.filter((node) => node.id !== nodeId));
      setEdges((eds) => eds.filter((edge) => 
        edge.source !== nodeId && edge.target !== nodeId
      ));
      setSelectedNode(null);
      setNodeMenuPosition(null);
    },
    [setNodes, setEdges]
  );

  const handleDuplicateNode = useCallback(
    (node) => {
      const newNode = {
        ...node,
        id: `${node.id}_copy_${Date.now()}`,
        position: {
          x: node.position.x + 50,
          y: node.position.y + 50,
        },
      };
      setNodes((nds) => [...nds, newNode]);
      setNodeMenuPosition(null);
    },
    [setNodes]
  );

  const handleUpdateNode = useCallback(
    (updatedNode) => {
      // Check if the node is being set to global
      const isBecomingGlobal = !nodes.find(n => n.id === updatedNode.id)?.data?.isGlobal && updatedNode.data.isGlobal;

      // Remove all incoming edges if the node is becoming global
      if (isBecomingGlobal) {
        setEdges(eds => eds.filter(edge => edge.target !== updatedNode.id));
      }

      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === updatedNode.id) {
            // Remove globalPrompt from the node data
            const { globalPrompt, ...nodeData } = updatedNode.data;
            
            return {
              ...node,
              type: updatedNode.type,
              data: {
                ...nodeData,
                label: nodeData.name,
                type: updatedNode.type,
                originalType: updatedNode.type
              },
              targetHandle: nodeData.isGlobal ? null : undefined,
            };
          }
          return node;
        })
      );
      setSelectedNode(null);
    },
    [setNodes, setEdges, nodes]
  );

  // Update edge validation
  const isValidConnection = useCallback((connection) => {
    // Get the target node
    const targetNode = nodes.find(node => node.id === connection.target);
    
    // Prevent connections to global nodes
    if (targetNode?.data?.isGlobal) {
      return false;
    }
    
    return true;
  }, [nodes]);

  // Add onInit handler
  const onInit = useCallback((instance) => {
    reactFlowInstance.current = instance;
    // Trigger fit view after a short delay to ensure nodes are properly laid out
    setTimeout(() => {
      instance.fitView({ 
        padding: 0.2,
        includeHiddenNodes: true,
        minZoom: 0.5,
        maxZoom: 2,
        duration: 800
      });
    }, 100);
  }, []);

  // Add function to handle new node creation
  const handleAddNode = useCallback((nodeType) => {
    // Ensure proper casing for nodeType
    let type = nodeType;
    if (type.includes('-')) {
      type = type.split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    const newNode = {
      id: `node-${Date.now()}`,
      type: type,
      position: {
        x: Math.random() * 500,
        y: Math.random() * 500,
      },
      style: {
        width: NODE_DIMENSIONS.width,
        padding: '15px',
        borderRadius: '8px',
      },
      data: {
        name: `New ${type}`,
        label: `New ${type}`,
        type: type,
        isGlobal: false,
        modelOptions: {
          modelType: 'smart',
          temperature: 0.2,
          interruptionThreshold: 0.5,
          skipUserResponse: false,
          block_interruptions: false,
          disableSilenceRepeat: false
        }
      }
    };

    // Add type-specific default data
    switch (type) {
      case 'End Call':
        newNode.data.prompt = 'Say goodbye to the user';
        break;
      case 'Transfer Call':
        newNode.data.text = 'Transferring the call now. Please hold..';
        newNode.data.transferNumber = '';
        break;
      case 'Vector DB Knowledge Base':
        newNode.data.kb = '';
        break;
      case 'Webhook':
        newNode.data.url = '';
        newNode.data.method = 'POST';
        newNode.data.body = '{}';
        break;
      default:
        break;
    }

    setNodes((nds) => [...nds, newNode]);
  }, [setNodes]);

  // Add resize handler
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = window.innerWidth - e.clientX;
      // Limit the width between 300px and 800px
      setJsonEditorWidth(Math.min(Math.max(300, newWidth), 800));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Update jsonEditorStyles to use dynamic width
  const getJsonEditorStyles = () => ({
    width: `${jsonEditorWidth}px`,
    height: '100%',
    position: 'fixed',
    right: 0,
    top: 0,
    backgroundColor: 'white',
    borderLeft: '1px solid #e5e7eb',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    overflowY: 'auto',
    transition: isResizing ? 'none' : 'width 0.1s ease-out'
  });

  return (
    <div className="dialog-flow-editor h-full flex flex-col">
      <div className="dialog-flow-header dialog-flow-sidebar-offset">
        <button 
          onClick={onBack}
          className="icon-button -ml-1"
          aria-label="Back to dialog flows"
        >
          <span className="text-xl">←</span>
        </button>
        
        <div className="ml-2 flex-1 min-w-0 flex items-center gap-4">
          <div className="min-w-0 flex-shrink">
            <h1 className="dialog-flow-title truncate">
              {dialogFlowName}
            </h1>
            <div className="dialog-flow-id truncate hidden sm:block">
              {dialogFlowId}
            </div>
          </div>
          
          <div className="hidden sm:block h-8 w-px bg-slate-200"></div>
          
          <div className="dialog-flow-count hidden sm:block">
            {nodes.length} nodes • {edges.length} edges
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <Button
            onClick={toggleJsonEditor}
            variant="secondary"
          >
            {showJsonEditor ? 'Hide JSON' : 'Show JSON'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="hidden md:inline-flex"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="dialog-flow-mobile-header md:hidden">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="icon-button" aria-label="Back to dialog flows">
            <span className="text-lg">←</span>
          </button>
          <div className="flex flex-col min-w-0">
            <div className="font-medium text-gray-900 truncate">{dialogFlowName}</div>
            <div className="text-gray-500 truncate">{dialogFlowId} {nodes.length} nodes • {edges.length} edges</div>
          </div>
        </div>
      </div>

      <div className="dialog-flow-canvas dialog-flow-sidebar-offset flex-1 relative" style={{ height: 'calc(100vh - 108px)' }}>
        <div className={`dialog-flow-toolbar ${(isAddNodeOpen || isGlobalPromptOpen) ? 'hidden md:flex' : 'flex'}`}>
          <Button
            onClick={() => setIsAddNodeOpen(true)}
            variant="secondary"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add new node
          </Button>
          <Button
            onClick={() => setIsGlobalPromptOpen(true)}
            variant="secondary"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
            </svg>
            Global Prompt
          </Button>
        </div>

        <ReactFlow
          style={{ width: '100%', height: '100%' }}
          proOptions={{ hideAttribution: true }}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgesDelete={onEdgesDelete}
          onConnect={handleConnect}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          onInit={onInit}
          fitView={false}
          fitViewOptions={{ 
            padding: 0.2,
            includeHiddenNodes: true,
            minZoom: 0.5,
            maxZoom: 2,
            duration: 800
          }}
          defaultViewport={{ 
            x: 0,
            y: 0,
            zoom: 1
          }}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          isValidConnection={isValidConnection}
          connectionMode="strict"
          connectOnClick={false}
          onNodeClick={onNodeClick}
          onSelectionChange={onSelectionChange}
          deleteKeyCode={['Backspace', 'Delete']}
          onClick={(event) => {
            if (event.target.classList.contains('react-flow__pane')) {
              setNodeMenuPosition(null);
              setSelectedNode(null);
            }
          }}
          onEdgeUpdate={handleEdgeLabelChange}
        >
          <Panel position="top-right" className="dialog-flow-layout-controls">
            <Button
              onClick={() => onLayout('TB')}
              variant="secondary"
              size="icon"
              title="Vertical Layout"
            >
              ↓
            </Button>
            <Button
              onClick={() => onLayout('LR')}
              variant="secondary"
              size="icon"
              title="Horizontal Layout"
            >
              →
            </Button>
          </Panel>
          <Controls />
          <ResponsiveMiniMap />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>

        {/* Save button - only show on mobile */}
        <div className={`fixed bottom-4 right-4 z-[9999] md:hidden ${(isAddNodeOpen || isGlobalPromptOpen) ? 'hidden' : 'block'}`}>
          <button
            onClick={handleSave}
            className="btn btn-primary"
          >
            Save Changes
          </button>
        </div>

        {/* Node Context Menu with updated styling */}
        {nodeMenuPosition && selectedNode && (
          <div
            className="dialog-node-menu fixed z-50"
            style={{
              left: nodeMenuPosition.x,
              top: nodeMenuPosition.y,
              minWidth: '150px',
              transform: 'translateY(-50%)', // Center vertically relative to node
            }}
          >
            <div className="p-2 space-y-1">
              <button
                onClick={() => {
                  handleCloseNodeMenu();
                }}
                className="dialog-node-menu-item"
              >
                Edit
              </button>
              <button
                onClick={() => handleDuplicateNode(selectedNode)}
                className="dialog-node-menu-item"
              >
                Duplicate
              </button>
              <button
                onClick={() => handleDeleteNode(selectedNode.id)}
                className="dialog-node-menu-item dialog-node-menu-danger"
              >
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Node Edit Modal */}
        {selectedNode && !nodeMenuPosition && (
          <NodeModal
            node={selectedNode}
            onClose={() => setSelectedNode(null)}
            onSave={handleUpdateNode}
            onDelete={() => handleDeleteNode(selectedNode.id)}
            onDuplicate={() => handleDuplicateNode(selectedNode)}
          />
        )}

        {/* Add the Global Prompt Modal */}
        <GlobalPromptModal
          isOpen={isGlobalPromptOpen}
          onClose={() => setIsGlobalPromptOpen(false)}
          globalPrompt={globalPrompt}
          onSave={handleSaveGlobalPrompt}
        />

        {/* Add Node Panel */}
        <AddNodePanel
          isOpen={isAddNodeOpen}
          onClose={() => setIsAddNodeOpen(false)}
          onAddNode={handleAddNode}
        />

        {/* Update JSON Editor Panel */}
        {showJsonEditor && (
          <div style={getJsonEditorStyles()}>
            <div 
              className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-teal-600 hover:opacity-50"
              onMouseDown={() => setIsResizing(true)}
              ref={resizeRef}
            />
            <div className="flex flex-col h-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">JSON Editor</h3>
                <div className="flex items-center gap-4">
                  {jsonError && (
                    <span className="text-red-500 text-sm">{jsonError}</span>
                  )}
                  <button
                    onClick={toggleJsonEditor}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    aria-label="Close JSON Editor"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
              <textarea
                value={jsonContent}
                onChange={handleJsonChange}
                className="flex-1 w-full p-4 font-mono text-sm app-field focus:outline-none"
                style={{
                  resize: 'none',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre',
                  overflowY: 'auto'
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowEditor; 
